import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { listTemplates, deleteTemplateApi } from '../api/api';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ConfirmationModal from '../components/ConfirmationModal';

const TemplateList = ({ setAlert }) => {
  const history = useHistory();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [editTarget, setEditTarget] = useState('');

  const handleEdit = (name) => setEditTarget(name);
  const confirmEdit = () => {
    history.push(`/templates/${encodeURIComponent(editTarget)}`);
    setEditTarget('');
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await listTemplates();
        setTemplates(res.templates || []);
      } catch (err) {
        console.error('Failed to load scorecards:', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load scorecards.' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [setAlert]);

  const handleDelete = (name) => {
    setDeleteInfo(name);
  };

  const confirmDelete = async () => {
    const name = deleteInfo;
    try {
      const res = await deleteTemplateApi(name);
      setTemplates((t) => t.filter((tmp) => tmp.templateName !== name));

      const campaignsMsg =
        res.removedFromCampaigns && res.removedFromCampaigns.length > 0
          ? ` Removed from campaigns: ${res.removedFromCampaigns.join(', ')}.`
          : '';

      setAlert?.({
        variant: 'success',
        heading: 'Template Deleted',
        text: `${res.message || 'Template deleted successfully.'}${campaignsMsg}`,
      });
    } catch (err) {
      console.error('Delete failed', err);
      const msg = err?.message || 'Failed to delete template.';
      setAlert?.({ variant: 'error', heading: 'Error', text: msg });
    } finally {
      setDeleteInfo(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scorecards</h1>
          <p className="text-gray-600 mt-1">Manage QA scorecards</p>
        </div>
        <div className="flex space-x-2">
          <button onClick={() => history.push('/templates/upload')} className="btn-outline">
            Upload Scorecard
          </button>
          <button onClick={() => history.push('/templates/new')} className="btn-primary">
            New Scorecard
          </button>
        </div>
      </div>
      <div className="card">
        {loading ? (
          <div className="text-center text-gray-500">Loading...</div>
        ) : templates.length === 0 ? (
          <p className="text-center text-gray-500">No scorecards found.</p>
        ) : (
          <div className="overflow-x-auto space-y-4">
            <input
              type="text"
              className="input-field mb-2"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {templates
                  .filter(t => t.templateName.toLowerCase().includes(search.toLowerCase()))
                  .map(t => (
                  <tr key={t.templateName} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.templateName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <button
                        onClick={() => handleEdit(t.templateName)}
                        className="btn-outline mr-2"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(t.templateName)}
                        className="btn-outline text-red-600 border-red-300 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmationModal
        open={!!editTarget}
        title="Edit Confirmation"
        message={`Are you sure you want to enter edit mode for ${editTarget}?`}
        confirmText="Continue"
        onClose={() => setEditTarget('')}
        onConfirm={confirmEdit}
      />
      <DeleteConfirmationModal
        open={!!deleteInfo}
        itemName={deleteInfo || ''}
        onClose={() => setDeleteInfo(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default TemplateList;
